import React, { useEffect, useState, useRef } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, ActivityIndicator, Platform, TextInput, ScrollView } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { MdLocationOn } from 'react-icons/md';
import PlatformIcon from '@cubemeet/ui/components/common/PlatformIcon';
import { getUserPublishedCards, getUserPublishedCardCities } from '@cubemeet/core/services/userProfileService';
import NearbyMap from './NearbyMap';

// Card type icons
const CARD_TYPE_ICONS: Record<string, string> = {
  'poi': 'place',
  'restaurant': 'restaurant',
  'transport': 'directions_bus',
  'lodging': 'hotel',
  'activity': 'event',
  'shopping': 'shopping_cart',
  'video': 'play_circle',
};

// Helper to extract coordinates from card
const extractCoords = (card: any): { lat: number; lng: number } | undefined => {
  if (!card) return undefined;
  
  const tryObj = (obj: any) => {
    if (!obj) return undefined;
    
    const parseNum = (v: any) => {
      if (typeof v === 'number') return v;
      if (typeof v === 'string' && v.trim()) {
        const n = Number(v);
        return Number.isNaN(n) ? undefined : n;
      }
      return undefined;
    };

    // Handle array [lng, lat] or [lat, lng] directly
    if (Array.isArray(obj) && obj.length >= 2) {
       const n1 = parseNum(obj[0]);
       const n2 = parseNum(obj[1]);
       if (n1 !== undefined && n2 !== undefined) {
          if (Math.abs(n1) > 90) return { lat: n2, lng: n1 };
          return { lat: n1, lng: n2 };
       }
    }

    // common shapes: { lat, lng } or { latitude, longitude } or { lat, lon }
    const lat1 = parseNum(obj.lat);
    const lng1 = parseNum(obj.lng) ?? parseNum(obj.lon);
    if (lat1 !== undefined && lng1 !== undefined) return { lat: lat1, lng: lng1 };

    const lat2 = parseNum(obj.latitude);
    const lng2 = parseNum(obj.longitude);
    if (lat2 !== undefined && lng2 !== undefined) return { lat: lat2, lng: lng2 };

    // coordinates array inside object
    if (Array.isArray(obj.coordinates) && obj.coordinates.length >= 2) {
      const a = obj.coordinates;
      const maybeLng = parseNum(a[0]);
      const maybeLat = parseNum(a[1]);
      if (maybeLat !== undefined && maybeLng !== undefined) return { lat: maybeLat, lng: maybeLng };
    }
    
    // string like "lat,lng" or "lng,lat"
    if (typeof obj === 'string' && obj.indexOf(',') !== -1) {
      const parts = obj.split(',').map(s => s.trim()).map(Number);
      if (parts.length >= 2 && parts.every(p => !Number.isNaN(p))) {
        if (Math.abs(parts[0]) <= 90) return { lat: parts[0], lng: parts[1] };
        return { lat: parts[1], lng: parts[0] };
      }
    }
    return undefined;
  };

  // check typical locations
  const candidates = [
    card, 
    card.base_payload, 
    card.custom_payload,
    card.base_payload?.location, 
    card.custom_payload?.location, 
    card.location, 
    card.address,
    card.base_payload?.geometry
  ];
  for (const c of candidates) {
    const r = tryObj(c);
    if (r) return r;
  }
  return undefined;
};

interface UserCard {
  id: string;
  creator_id: string;
  card_type: string;
  title: string;
  description?: string;
  cover_image?: string;
  image_url?: string;
  imageUrl?: string;
  image?: string;
  image_gallery?: Array<string | { url?: string }>;
  base_payload?: any;
  custom_payload?: any;
  address?: string;
  rating?: number;
  tags?: string[];
  created_at: string;
}

interface UserCardsProps {
  userId: string;
}

// 从卡片数据中提取封面图片 - 与 CardListPage 保持一致
const pickCoverImage = (card: UserCard): string | undefined => {
  try {
    const c = card as any;
    const base = c.base_payload || {};
    const isVideoCard = c.card_type === 'video' || !!base.embed;

    // 視頻卡：優先使用 base_payload 裏面的縮略圖
    if (isVideoCard) {
      const embed = base.embed || {};
      const videoThumb = base.imageUrl || embed.poster;
      if (videoThumb) return String(videoThumb);
    }

    // 1. 先检查顶层字段
    if (c.cover_image) return String(c.cover_image);
    if (c.imageUrl) return String(c.imageUrl);
    if (c.image_url) return String(c.image_url);
    if (c.image) return String(c.image);

    // 2. 检查 image_gallery 数组的第一张图
    if (Array.isArray(c.image_gallery) && c.image_gallery.length > 0) {
      const first = c.image_gallery[0];
      if (typeof first === 'string') return first;
      if (first && typeof first.url === 'string') return first.url;
    }

    // 3. 检查 base_payload 中的图片
    if (base.cover_image) return String(base.cover_image);
    if (base.imageUrl) return String(base.imageUrl);
    if (base.image_url) return String(base.image_url);
    if (base.image) return String(base.image);
    if (base.cover) return String(base.cover);

    // 4. 检查 base_payload.image_gallery
    if (Array.isArray(base.image_gallery) && base.image_gallery.length > 0) {
      const first = base.image_gallery[0];
      if (typeof first === 'string') return first;
      if (first && typeof first.url === 'string') return first.url;
    }

    return undefined;
  } catch {
    return undefined;
  }
};

const UserCards: React.FC<UserCardsProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<UserCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [showCityFilter, setShowCityFilter] = useState(false);
  const [paging, setPaging] = useState(false);
  const containerRef = useRef<View>(null);

  const cardTypes = [
    { key: 'poi', label: '景點', icon: 'place' },
    { key: 'restaurant', label: '餐廳', icon: 'restaurant' },
    { key: 'lodging', label: '住宿', icon: 'hotel' },
    { key: 'activity', label: '活動', icon: 'event' },
    { key: 'shopping', label: '購物', icon: 'shopping-cart' },
  ];

  useEffect(() => {
    fetchAvailableCities();
  }, [userId]);

  useEffect(() => {
    loadCards(1);
  }, [userId, searchQuery, selectedTypes, selectedCities]);

  const fetchAvailableCities = async () => {
    try {
      const cities = await getUserPublishedCardCities(userId);
      setAvailableCities(cities);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const loadCards = async (pageNum: number, isPageSwitch = false) => {
    try {
      if (isPageSwitch) {
        setPaging(true);
        // Scroll to top of container
        if (Platform.OS === 'web') {
          setTimeout(() => {
            try {
              // @ts-ignore
              const node = containerRef.current;
              if (node && node.getBoundingClientRect) {
                const rect = node.getBoundingClientRect();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                // Scroll to show the search bar (top of container)
                // Adjust offset to account for sticky headers if any, but keep tabs hidden
                const targetY = rect.top + scrollTop - 10;
                window.scrollTo({ top: targetY, behavior: 'smooth' });
              }
            } catch (e) {
              // ignore
            }
          }, 50);
        }
      } else {
        setLoading(true);
      }

      const pageSize = 10;
      const { data, count } = await getUserPublishedCards(userId, {
        page: pageNum,
        pageSize,
        sortBy: 'created_at',
        sortOrder: 'desc',
        searchQuery,
        cardType: selectedTypes,
        cities: selectedCities,
      });

      // DEBUG: 输出第一张卡的数据结构
      if (data.length > 0) {
        console.log('[UserCards] First card data:', JSON.stringify(data[0], null, 2));
        console.log('[UserCards] Cover result:', pickCoverImage(data[0]));
        console.log('[UserCards] Coords result:', extractCoords(data[0]));
      }

      setCards(data);
      setTotalCount(count);
      setTotalPages(Math.ceil(count / pageSize));
      setPage(pageNum);
    } catch (error) {
      console.error('加载卡片失败:', error);
    } finally {
      setLoading(false);
      setPaging(false);
    }
  };

  const handleTypeToggle = (type: string) => {
    setSelectedTypes(prev => {
      const newTypes = prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type];
      return newTypes;
    });
    setPage(1);
  };

  const getPageWindow = (total: number, current: number) => {
    const maxVisible = 5;
    if (total <= maxVisible) return Array.from({ length: total }).map((_, i) => i + 1);
    let start = current - Math.floor(maxVisible / 2);
    if (start < 1) start = 1;
    let end = start + maxVisible - 1;
    if (end > total) {
      end = total;
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }).map((_, i) => start + i);
  };

  return (
    <View style={styles.container} ref={containerRef}>
      <View style={styles.searchContainer}>
        <View style={styles.searchRow}>
          <View style={styles.searchBox}>
            <PlatformIcon name="search" size={20} color="#6B7280" />
            <TextInput
              style={styles.searchInput}
              placeholder="搜索卡片..."
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>
          <TouchableOpacity 
            style={[styles.filterButton, selectedCities.length > 0 && styles.filterButtonActive]}
            onPress={() => setShowCityFilter(!showCityFilter)}
            accessibilityLabel="城市过滤"
          >
            <PlatformIcon name="tune" size={20} color={selectedCities.length > 0 ? '#0B61D6' : '#6B7280'} family="MaterialIcons" />
            {selectedCities.length > 0 && (
              <View style={styles.filterBadge}>
                <Text style={styles.filterBadgeText}>{selectedCities.length}</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
        {showCityFilter && (
          <View style={styles.cityFilterContainer}>
            <View style={styles.cityFilterHeader}>
              <TouchableOpacity onPress={() => setShowCityFilter(false)}>
                <PlatformIcon name="close" size={20} color="#6B7280" family="MaterialIcons" />
              </TouchableOpacity>
            </View>
            <View style={styles.cityChipsWrap}>
              {availableCities.map((city) => {
                const isSelected = selectedCities.includes(city);
                return (
                  <TouchableOpacity
                    key={city}
                    style={[styles.cityChip, isSelected && styles.cityChipSelected]}
                    onPress={() => {
                      setSelectedCities(prev => 
                        isSelected ? prev.filter(c => c !== city) : [...prev, city]
                      );
                    }}
                  >
                    <Text style={[styles.cityChipText, isSelected && styles.cityChipTextSelected]}>
                      {city}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtersContainer}>
        <View style={styles.filterGroup}>
          {cardTypes.map(type => (
            <TouchableOpacity
              key={type.key}
              style={[styles.filterChip, selectedTypes.includes(type.key) && styles.filterChipActive]}
              onPress={() => handleTypeToggle(type.key)}
            >
              <PlatformIcon
                name={type.icon}
                size={16}
                color={selectedTypes.includes(type.key) ? '#0B61D6' : '#6B7280'}
              />
              <Text style={[styles.filterChipText, selectedTypes.includes(type.key) && styles.filterChipTextActive]}>
                {type.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      {loading && cards.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#ff6b35" />
          <Text style={styles.loadingText}>加载卡片中...</Text>
        </View>
      ) : cards.length === 0 ? (
        <View style={styles.emptyContainer}>
          <PlatformIcon name="style" size={48} color="#D1D5DB" />
          <Text style={styles.emptyText}>
            {searchQuery || selectedTypes.length > 0 ? '没有找到匹配的卡片' : '还没有发布任何卡片'}
          </Text>
        </View>
      ) : (
        <>
          <View style={styles.cardsContainer}>
            {paging && (
              <View style={styles.pageTransitionOverlay} pointerEvents="none">
                <ActivityIndicator size="small" color="#0B61D6" />
                <Text style={styles.pageTransitionText}>切换页面中...</Text>
              </View>
            )}
            {cards.map((card) => {
              const coverUrl = pickCoverImage(card);
              const coords = extractCoords(card);
              // 如果封面是占位图，优先显示地图
              const isPlaceholder = coverUrl && (
                coverUrl.includes('placeholder') || 
                coverUrl.includes('default') ||
                coverUrl.includes('/assets/')
              );
              const shouldShowMap = !coverUrl || isPlaceholder;
              return (
              <TouchableOpacity
                key={card.id}
                style={styles.cardItem}
                onPress={() => navigate(`/card/${card.id}`)}
                activeOpacity={0.7}
              >
                {/* 卡片封面 */}
                <View style={styles.cardCover}>
                  {!shouldShowMap && coverUrl ? (
                    <Image source={{ uri: coverUrl }} style={styles.cardImage} resizeMode="cover" />
                  ) : coords ? (
                    <View style={{ width: '100%', height: '100%', overflow: 'hidden' }} pointerEvents="none">
                      <NearbyMap
                        key={`map-${card.id}`}
                        center={[coords.lat, coords.lng]}
                        spots={[]}
                        primaryLabel={card.title}
                        primaryName={card.title}
                        enableOSM={false}
                        showInterPointDistances={false}
                        height="100%"
                        showFullscreenButton={false}
                        showControls={false}
                        embedded={true}
                      />
                    </View>
                  ) : (
                    <View style={styles.cardImagePlaceholder}>
                      <PlatformIcon name="image" size={24} color="#9CA3AF" />
                    </View>
                  )}
                  {/* 类型徽章 */}
                  <View style={styles.cardTypeBadge}>
                    <PlatformIcon 
                      name={CARD_TYPE_ICONS[card.card_type] || 'place'} 
                      size={12} 
                      color="#fff" 
                    />
                  </View>
                </View>

                {/* 卡片内容 */}
                <View style={styles.cardInfo}>
                  <Text style={styles.cardTitle} numberOfLines={2}>
                    {card.title || '未命名卡片'}
                  </Text>

                  {card.address && (
                    <View style={styles.cardAddress}>
                      <MdLocationOn size={14} color="#0ea5a4" />
                      <Text style={styles.cardAddressText} numberOfLines={1}>
                        {card.address}
                      </Text>
                    </View>
                  )}

                  {/* 标签 */}
                  {Array.isArray(card.tags) && card.tags.length > 0 && (
                    <View style={styles.cardTags}>
                      {card.tags.slice(0, 2).map((tag, i) => (
                        <View key={i} style={styles.cardTag}>
                          <Text style={styles.cardTagText}>#{tag}</Text>
                        </View>
                      ))}
                      {card.tags.length > 2 && (
                        <Text style={styles.cardTagMore}>+{card.tags.length - 2}</Text>
                      )}
                    </View>
                  )}
                </View>
              </TouchableOpacity>
            );
            })}
          </View>

          {/* 分页控件 */}
          {totalPages > 1 && (
            <View style={styles.paginationContainer}>
              <TouchableOpacity
                style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]}
                onPress={() => { if (page > 1) loadCards(1, true); }}
              >
                <Text style={styles.pageButtonText}>«</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]}
                onPress={() => { if (page > 1) loadCards(page - 1, true); }}
              >
                <Text style={styles.pageButtonText}>‹</Text>
              </TouchableOpacity>

              {getPageWindow(totalPages, page).map((p) => (
                <TouchableOpacity
                  key={p}
                  style={[styles.pageButton, page === p && styles.pageButtonActive]}
                  onPress={() => { if (p !== page) loadCards(p, true); }}
                >
                  <Text style={[styles.pageButtonText, page === p && styles.pageButtonTextActive]}>{p}</Text>
                </TouchableOpacity>
              ))}

              <TouchableOpacity
                style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]}
                onPress={() => { if (page < totalPages) loadCards(page + 1, true); }}
              >
                <Text style={styles.pageButtonText}>›</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]}
                onPress={() => { if (page < totalPages) loadCards(totalPages, true); }}
              >
                <Text style={styles.pageButtonText}>»</Text>
              </TouchableOpacity>
            </View>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  searchContainer: { paddingHorizontal: 12, paddingBottom: 12 },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 20, paddingHorizontal: 16, paddingVertical: 8 },
  searchInput: { flex: 1, marginLeft: 12, fontSize: 14, color: '#111827', outlineWidth: 0, outlineColor: 'transparent' },
  
  filterButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#F3F4F6', alignItems: 'center', justifyContent: 'center', position: 'relative' as any },
  filterButtonActive: { backgroundColor: '#E6F0FF' },
  filterBadge: { position: 'absolute' as any, top: -4, right: -4, backgroundColor: '#FB7A4A', borderRadius: 10, minWidth: 18, height: 18, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4 },
  filterBadgeText: { color: '#fff', fontSize: 10, fontWeight: '700' as any },
  
  cityFilterContainer: { marginTop: 12, backgroundColor: '#F9FAFB', borderRadius: 12, padding: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  cityFilterHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 8 },
  cityChipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  cityChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#fff', borderWidth: 1, borderColor: '#E5E7EB' },
  cityChipSelected: { backgroundColor: '#0B61D6', borderColor: '#0B61D6' },
  cityChipText: { fontSize: 13, color: '#374151', fontWeight: '500' as any },
  cityChipTextSelected: { color: '#fff', fontWeight: '600' as any },

  filtersContainer: { paddingHorizontal: 12, marginBottom: 12 },
  filterGroup: { flexDirection: 'row', alignItems: 'center', paddingRight: 24 },
  filterChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, marginRight: 8 },
  filterChipActive: { backgroundColor: '#E6F0FF' },
  filterChipText: { fontSize: 13, color: '#6B7280', marginLeft: 4 },
  filterChipTextActive: { color: '#0B61D6', fontWeight: '600' },

  container: {
    paddingVertical: 8,
  },

  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    gap: 8,
  },
  loadingText: {
    fontSize: 13,
    color: '#666',
  },

  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
    gap: 12,
  },
  emptyText: {
    fontSize: 14,
    color: '#9CA3AF',
  },

  cardsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    position: 'relative',
  },

  pageTransitionOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.85)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    borderRadius: 12,
  },
  pageTransitionText: {
    marginTop: 8,
    fontSize: 13,
    color: '#4B5563',
  },

  cardItem: {
    width: '48%',
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 12,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
        elevation: 2,
      },
    }),
  },

  cardCover: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#F3F4F6',
    position: 'relative',
    overflow: 'hidden',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },

  cardImage: {
    width: '100%',
    height: '100%',
  },

  cardImagePlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
  },

  cardTypeBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    borderRadius: 12,
    padding: 4,
  },

  cardInfo: {
    padding: 10,
  },

  cardTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 6,
    lineHeight: 18,
  },

  cardAddress: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },

  cardAddressText: {
    fontSize: 12,
    color: '#0ea5a4',
    marginLeft: 4,
    flex: 1,
  },

  cardTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },

  cardTag: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginRight: 4,
    marginBottom: 2,
  },

  cardTagText: {
    fontSize: 11,
    color: '#6B7280',
  },

  cardTagMore: {
    fontSize: 11,
    color: '#9CA3AF',
    marginLeft: 2,
  },

  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 12,
    flexWrap: 'nowrap',
    alignSelf: 'center',
    marginHorizontal: 12,
  },
  pageButton: { minWidth: 36, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 8, marginHorizontal: 4, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center' },
  pageButtonActive: { backgroundColor: '#ff6b35', borderColor: '#ff6b35' },
  pageButtonDisabled: { opacity: 0.5 },
  pageButtonText: { fontSize: 14, color: '#111827' },
  pageButtonTextActive: { color: '#fff', fontWeight: '700' },
});

export default UserCards;
